import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Listbox, Transition } from '@headlessui/react';
import exchangeCtx from 'context/exchange';
import accountCtx from 'context/account';
import { EXCHANGES } from 'config/constants';
import { CheckIcon, AngleDownIcon } from 'components/icons';

const EXCHANGES_ARRAY = [];
const EXCHANGES_LABELS = {};

Object.keys(EXCHANGES).forEach(key => {
	const exchangeObject = EXCHANGES[key];
	if (exchangeObject.tradeEnabled) {
		EXCHANGES_ARRAY.push(key);
		EXCHANGES_LABELS[key] = exchangeObject.displayName;
	}
});

const ExchangeSwitcher = ({ variant = 'dropdown', onChange }) => {
	const { exchangeKey, setExchange } = useContext(exchangeCtx);
	const { accountdata } = useContext(accountCtx);
	const [showPicker, setShowPicker] = useState(false);

	const usedExchanges = EXCHANGES_ARRAY.filter(key => accountdata?.apikeys[key]);

	const handleChange = newKey => {
		if (onChange) {
			onChange(newKey);
		}
		setExchange(newKey);
		setShowPicker(false);
	};

	if (variant === 'fullscreen') {
		// Single (or zero) exchange: static, non-interactive pill.
		if (usedExchanges.length < 2) {
			return (
				<div className="rounded border border-indigo-300 px-4 py-2 font-normal uppercase text-white">
					{exchangeKey}
				</div>
			);
		}

		// Multiple exchanges: button opens a full-screen "Select Exchange" selector.
		let picker;
		if (showPicker) {
			picker = (
				<div className="fixed inset-0 z-50 flex flex-col bg-black native-mobile-padding">
				<div className="py-4 text-center text-xl font-bold uppercase tracking-widest text-white">Select Exchange</div>
				<ul className="grow overflow-y-auto divide-y divide-gray-700 border-t border-gray-700 px-3">
					{usedExchanges.map(key => (
						<li
							key={key}
							onClick={handleChange.bind(null, key)}
							className={clsx('flex cursor-pointer select-none items-center justify-between py-3 px-2', {
								'bg-blue-500 text-white rounded': key === exchangeKey,
							})}
						>
							<span className="font-semibold uppercase">{key}</span>
							<span className={clsx('text-xs', key === exchangeKey ? 'text-white/75' : 'text-gray-400')}>
								{EXCHANGES_LABELS[key]}
							</span>
						</li>
					))}
				</ul>
				<div className="border-t border-gray-700 p-3">
					<button
						type="button"
						onClick={() => setShowPicker(false)}
						className="btn btn-bordered w-full uppercase tracking-widest"
					>
						Close
					</button>
				</div>
			</div>
			);
		}

		return (
			<div>
				<button
					type="button"
					onClick={() => setShowPicker(true)}
					className="form-input relative flex items-center gap-2 rounded border border-indigo-300 bg-transparent px-4 py-2 font-normal text-white"
				>
					<span className="block truncate uppercase">{exchangeKey}</span>
					<AngleDownIcon className="text-yellow-400 w-5 h-5" />
				</button>
				{picker}
			</div>
		);
	}

	// variant === 'dropdown' (desktop) — moved verbatim from Header.jsx.
	const buttonClassName = clsx(
		'form-input w-full font-normal rounded border border-indigo-300 bg-transparent px-4 py-3 text-white placeholder:text-white/25',
		{
			'cursor-default': usedExchanges.length < 2,
			'hover:bg-blue-300 hover:text-black': usedExchanges.length > 1,
		},
	);

	const options = usedExchanges.map(exchange => (
		<Listbox.Option
			className={({ active }) =>
				`relative cursor-default select-none py-2 pl-10 pr-4 cursor-pointer ${
					active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
				}`
			}
			value={exchange}
			key={exchange}
		>
			{({ selected }) => {
				let checkIcon;
				if (selected) {
					checkIcon = (
						<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
							<CheckIcon className="text-yellow-400 w-6 h-6" />
						</span>
					);
				}

				return (
					<>
						<span className={`block truncate uppercase ${selected ? 'font-medium' : 'font-normal'}`}>
							{EXCHANGES_LABELS[exchange]}
						</span>
						{checkIcon}
					</>
				);
			}}
		</Listbox.Option>
	));

	return (
		<div>
			<Listbox value={exchangeKey} onChange={handleChange} className="flex flex-col gap-1 font-semibold">
				<div className="relative">
					<Listbox.Button
						className={buttonClassName}
						onClick={e => {
							if (usedExchanges.length < 2) {
								e.preventDefault();
							}
						}}
					>
						<span className="block truncate text-left uppercase">{exchangeKey}</span>
					</Listbox.Button>

					<Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
						<Listbox.Options className="absolute right-0 mt-12 w-300 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
							{options}
						</Listbox.Options>
					</Transition>
				</div>
			</Listbox>
		</div>
	);
};

ExchangeSwitcher.propTypes = {
	variant: PropTypes.oneOf(['dropdown', 'fullscreen']),
	onChange: PropTypes.func,
};

export default ExchangeSwitcher;
